import type {PbrExport} from "@cm/material-maps-exporter"
import {DataObjectAssignmentType} from "@cm/utils/data-object-assignment"
import type {DataObjectDefinitions} from "@cm/utils/data-object"

export const CM_TO_INCH = 1 / 2.54

export type MapType = PbrExport.Types.TextureType

export const MATERIAL_THUMBNAIL_TYPE = [
    DataObjectAssignmentType.MaterialThumbnail_10x10,
    DataObjectAssignmentType.MaterialThumbnail_15x15,
    DataObjectAssignmentType.MaterialThumbnail_20x20,
    DataObjectAssignmentType.MaterialThumbnail_30x30,
    DataObjectAssignmentType.MaterialThumbnail_7x7Inches,
    DataObjectAssignmentType.MaterialThumbnail_9x9Inches,
    DataObjectAssignmentType.MaterialThumbnail_16x16Inches,
    DataObjectAssignmentType.MaterialThumbnail_8x8Inches,
    DataObjectAssignmentType.MaterialThumbnail_13_5x13_5Inches,
    DataObjectAssignmentType.MaterialThumbnail_27x27Inches,
    DataObjectAssignmentType.MaterialThumbnail_32x32Inches,
] as const

export const RENDER_ASSET_TYPE = [DataObjectAssignmentType.MaterialTileableRender, ...MATERIAL_THUMBNAIL_TYPE] as const

export const MAPS_EXPORT_MAP_ASSET_TYPE = `${DataObjectAssignmentType.MaterialMapsExport}Map` as const
export const MAPS_EXPORT_INFO_ASSET_TYPE = `${DataObjectAssignmentType.MaterialMapsExport}Info` as const

export type RenderAssetType = (typeof RENDER_ASSET_TYPE)[number]
export type MaterialThumbnailType = (typeof MATERIAL_THUMBNAIL_TYPE)[number]
export type MapsExportAssetType = typeof DataObjectAssignmentType.MaterialMapsExport
export type MapsExportMapAssetType = typeof MAPS_EXPORT_MAP_ASSET_TYPE
export type MapsExportInfoAssetType = typeof MAPS_EXPORT_INFO_ASSET_TYPE
export type AssetType = RenderAssetType | MapsExportAssetType | MapsExportMapAssetType | MapsExportInfoAssetType

export function isAssignmentTypeValidAssetType(x: DataObjectAssignmentType): x is RenderAssetType | MapsExportAssetType {
    return ([...RENDER_ASSET_TYPE, DataObjectAssignmentType.MaterialMapsExport] as Readonly<DataObjectAssignmentType[]>).includes(x)
}

export function isRenderAssetType(x: AssetType): x is RenderAssetType {
    return RENDER_ASSET_TYPE.includes(x as RenderAssetType)
}

export function isMaterialThumbnailType(x: AssetType): x is MaterialThumbnailType {
    return MATERIAL_THUMBNAIL_TYPE.includes(x as MaterialThumbnailType)
}

export type MaterialData = {
    articleId: string | null
    name: string | null
    rangeInfo: {
        name: string | null
        otherInfo: string | null
    }
    widthCm: number | null
    heightCm: number | null
    revisionNumber: number | null
}

type AssetDataT_<T extends AssetType> = {
    type: T
    organizationName: string
    materialData: MaterialData
}

export type ImageResolution = DataObjectDefinitions.DerivedImageResolution | "original"
export type ImageFormat = Exclude<DataObjectDefinitions.ImageFormat, "hdr" | "ply" | "drc" | "obj">

/**
 * The settings an export is configured by, as `@cm/material-maps-exporter` defines them — the only part
 * of an export any filename is derived from. All three maps-export assets take these plus their own
 * discriminator, so none of them takes a container; a stored config and a conversion request both
 * satisfy this, so either can be named directly.
 */
export type ExportSettings = PbrExport.Types.Settings

export type AssetDataT<T extends AssetType> = T extends MapsExportAssetType
    ? AssetDataT_<T> & {exportSettings: ExportSettings}
    : T extends MapsExportMapAssetType
      ? AssetDataT_<T> & {exportSettings: ExportSettings; mapType: MapType}
      : T extends MapsExportInfoAssetType
        ? AssetDataT_<T> & {exportSettings: ExportSettings; fileFormat: PbrExport.Types.InfoFileFormat}
        : AssetDataT_<T> & {imageResolution: ImageResolution; imageFormat: ImageFormat}
export type AssetData<T extends AssetType = AssetType> = AssetDataT<T>

export type AssetDataMapsExport = AssetData<MapsExportAssetType>

export const UNDEFINED_FIELD_SYMBOL = "UNDEFINED"
export type FilenameSchema = string
export type OrganizationName = string

type FilenameSchemaEntry_<T extends AssetType> = {
    type: T
    schema: FilenameSchema
    options?: {
        fractionDigits?: number
        decimalSeparator?: string
        imageFormatSpelling?: {
            jpeg?: "jpg" | "jpeg"
            tiff?: "tiff" | "tif"
        }
    }
    isOtherInfoSet?: boolean
}

export type FilenameSchemaEntry<T extends AssetType = AssetType> = T extends MapsExportAssetType
    ? FilenameSchemaEntry_<T> & {
          exportFormat?: ExportSettings["format"]
          exportResolution?: ExportSettings["resolution"]
      }
    : T extends MapsExportMapAssetType
      ? FilenameSchemaEntry_<T> & {
            exportFormat?: ExportSettings["format"]
            exportResolution?: ExportSettings["resolution"]
            mapType?: MapType
        }
      : T extends MapsExportInfoAssetType
        ? FilenameSchemaEntry_<T> & {
              exportFormat?: ExportSettings["format"]
              exportResolution?: ExportSettings["resolution"]
              fileFormat?: PbrExport.Types.InfoFileFormat
          }
        : FilenameSchemaEntry_<T> & {
              imageFormat?: AssetData<RenderAssetType>["imageFormat"]
              imageResolution?: AssetData<RenderAssetType>["imageResolution"]
          }
